/**
 * Страница 3-шагового мастера заказа обучения (`/orders/:id/training`, гард
 * company+individual). Мастер применяется ТОЛЬКО к клиенту-компании (Б4, design D9:
 * «поток физлица не трогаем»). Физлицо ИЗОЛИРОВАНО от мастера компании: ему показывается
 * информационное состояние (осознанно вне scope фазы 2), а не сломанный шаг «Данные
 * компании» (у физлица нет реквизитов организации). Серверный гейт «физлицо → только
 * обучение» при этом сохраняется (см. handleCreateOrder). Для компании: загружает каркас
 * заказа (OrderDetail) и мастер (TrainingWizard). submitted-заказ приходит с
 * `canEdit:false` — вместо мастера показывается экран «Заказ отправлен» с блоком согласий
 * (centrika-docs, MR !36), а не режим просмотра. Не-training заказ — мягкая ошибка.
 */
import { useEffect, useState } from 'react'
import { ArrowLeft } from 'lucide-react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import {
  ApiError,
  ordersApi,
  trainingApi,
  type OrderDetail,
  type TrainingWizard,
} from '@/lib/api'
import { useAuth } from '@/app/auth-context'
import { getErrorMessage } from '@/lib/errors'
import { serviceLabel } from '@/lib/orders'
import {
  PROGRAMS_STEP_HINT,
  PROGRAMS_STEP_HINT_INDIVIDUAL,
  REQUEST_SENT_NOTICE,
  TrainingRequest,
  TrainingSteps,
  initialTrainingStep,
} from '@/training/training-request'
import { PAPERWORK_PENDING, TrainingPaperwork } from '@/training/training-paperwork'
import { TrainingStartNotice, type TrainingStartState } from '@/training/training-order-start'
import { TrainingChangeEditor, TrainingChangeHistory, TrainingChangeNotice } from '@/training/training-change'
import { AnketaCancelOrderButton } from '@/orders/order-cancel'
import { OrderQuestionButton } from '@/questions/ask-question'
import { PageHead } from '@/design-system/components/layout/page-head'
import { Button } from '@/design-system/components/primitives/button'
import { Alert } from '@/design-system/components/feedback/alert'

/**
 * Текст под заголовком мастера по этапу заявки (training-order-filling): черновик не по
 * абонентскому обслуживанию — сначала заявка менеджеру; «На заполнении» — данные слушателей.
 */
function wizardSubtitle(order: OrderDetail, wizard: TrainingWizard): string {
  // Физлицо (individual-training-order): слушатель — он сам, сотрудников нет.
  if (wizard.selfPersonId) {
    if (order.dataStatus === 'filling') return 'Заполните свои данные и подтвердите их'
    return `${PROGRAMS_STEP_HINT_INDIVIDUAL} Затем отправьте заявку менеджеру и заполните свои данные.`
  }
  if (order.dataStatus === 'filling') return 'Заполните данные слушателей и подтвердите их'
  // Подсказка выбора программ — текст заказчицы (ответ 04.11 к п.4.1–4.3), вместо прежнего «Выберите
  // программы и сотрудников, проверьте данные и отправьте заказ» и плашки на шаге.
  if (order.dataStatus === 'draft' && !wizard.subscription) {
    return `${PROGRAMS_STEP_HINT} Затем отправьте заявку менеджеру и заполните данные слушателей.`
  }
  return PROGRAMS_STEP_HINT
}

export function OrderTrainingRequestPage() {
  const navigate = useNavigate()
  const location = useLocation()
  // Что сделал запуск заказа (training-order-entry-points): заполнено по срокам или сотрудник по
  // умолчанию — из состояния перехода, только у черновика.
  const { id } = useParams<{ id: string }>()
  // Состояние перехода — только для своего заказа и один раз: после чтения история очищается,
  // чтобы обновление страницы или возврат не повторяли плашку и сотрудника по умолчанию.
  const [startState] = useState<TrainingStartState>(() => {
    const state = location.state as TrainingStartState | null
    return state && state.orderId === id ? state : {}
  })
  const startedHere = Boolean(startState.prefill || startState.prefillFailed || startState.presetPerson)
  useEffect(() => {
    if (location.state) navigate(location.pathname, { replace: true, state: null })
    // Только при открытии страницы.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  const { context, refresh } = useAuth()
  const individual = context?.user.role === 'individual'
  const [order, setOrder] = useState<OrderDetail | null>(null)
  const [wizard, setWizard] = useState<TrainingWizard | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  /** Заказ отправлен ПРЯМО СЕЙЧАС — мастер сообщает об этом колбэком (MR !36). */
  const [justSubmitted, setJustSubmitted] = useState(false)
  /** Текущий шаг мастера — для шагов в шапке страницы (ТЗ п.1.1, картинка 2). */
  const [step, setStep] = useState(0)
  // Изменения состава заказа «На заполнении» (training-change-approval): редактор вместо мастера;
  // после предложения или решения по нему мастер открывается заново с перечитанным составом (тот же шаг).
  const [editing, setEditing] = useState(false)
  const [wizardVersion, setWizardVersion] = useState(0)
  // Состояние «Заказ отправлен»: только что отправили либо заказ был отправлен раньше
  // (`canEdit:false` — «На проверке» / «В работе» / «Готово»).
  const submittedView = justSubmitted || (wizard != null && !wizard.canEdit)

  useEffect(() => {
    if (!id) return
    // Физлицо заказывает обучение в том же мастере (individual-training-order): первый шаг — его
    // данные, слушатель — он сам (selfPersonId мастера).
    const controller = new AbortController()
    setLoading(true)
    setError(null)
    Promise.all([ordersApi.get(id, controller.signal), trainingApi.getWizard(id, controller.signal)])
      .then(([ord, wiz]) => {
        if (controller.signal.aborted) return
        setOrder(ord)
        setWizard(wiz)
        // Шаг в шапке — сразу тот, с которого откроется мастер (без кадра с первым шагом). Черновик,
        // заполненный по срокам или со своим сотрудником, — сразу на шаге «Программы и сотрудники».
        setStep(ord.dataStatus === 'draft' && startedHere ? 1 : initialTrainingStep(ord))
      })
      .catch((err) => {
        if (!controller.signal.aborted) setError(getErrorMessage(err, 'Не удалось загрузить заказ'))
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false)
      })
    return () => controller.abort()
  }, [id, startedHere])

  /** Заказ и мастер изменились на сервере (изменения состава): перечитать мастер и меню. */
  async function applyChanged(next: OrderDetail) {
    setEditing(false)
    setOrder(next)
    try {
      setWizard(await trainingApi.getWizard(next.id))
    } catch {
      // Мастер остаётся прежним — состав перечитается при следующем открытии.
    }
    setWizardVersion((v) => v + 1)
    void refresh()
  }

  /** Сервер отказал (решение уже принято с другой стороны): заказ — в соответствие с сервером. */
  async function reloadAfterFailure(orderId: string) {
    try {
      await applyChanged(await ordersApi.get(orderId))
    } catch {
      // Ошибка уже показана; страница остаётся как была.
    }
  }

  async function decideChange(action: 'approve' | 'reject' | 'withdraw', reason = '') {
    if (!order) return
    let next: OrderDetail
    try {
      next = await (action === 'approve'
        ? trainingApi.approveChange(order.id)
        : action === 'reject'
          ? trainingApi.rejectChange(order.id, reason)
          : trainingApi.withdrawChange(order.id))
    } catch (err) {
      void reloadAfterFailure(order.id)
      throw err
    }
    await applyChanged(next)
  }

  return (
    <div>
      <div className="mb-4">
        <Button
          variant="secondary"
          size="sm"
          onClick={() => navigate('/orders')}
          leftIcon={<ArrowLeft className="size-4 stroke-current" aria-hidden />}
        >
          К моим заказам
        </Button>
      </div>

      {error ? (
        <Alert variant="danger">{error}</Alert>
      ) : loading || !order || !wizard ? (
        <p className="py-10 text-center text-txt-secondary">Загрузка…</p>
      ) : order.service !== 'training' ? (
        <Alert variant="danger">
          Это заказ услуги «{serviceLabel(order.service)}» — мастер обучения к нему не применим.
        </Alert>
      ) : (
        <>
          {/* Текст под заголовком не отображается в состоянии «Заказ отправлен» (MR !36):
              заказ уже отправлен, и инструкция по заполнению мастера потеряла смысл. Сам
              заголовок и кнопка «К моим заказам» остаются. */}
          {/* Шаги — в шапке, справа от заголовка (ТЗ п.1.1, картинка 2); на узком экране — под
              заголовком компактной строкой «Шаг N из 4». У отправленного заказа шагов нет. */}
          <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
            <PageHead
              className="mb-0 min-w-0 flex-1"
              title="Заказ обучения"
              subtitle={submittedView ? undefined : wizardSubtitle(order, wizard)}
              actions={
                submittedView ? undefined : (
                  <div className="flex flex-wrap justify-end gap-2">
                    {/* Вопрос по заказу (client-questions; ТЗ п.9.7). */}
                    <OrderQuestionButton order={order} onCreated={() => void refresh()} />
                    <AnketaCancelOrderButton order={order} onCancelled={() => void refresh()} />
                  </div>
                )
              }
            />
            {submittedView ? null : (
              <div className="w-full nw:w-1/2">
                <TrainingSteps current={step} individual={individual} />
              </div>
            )}
          </div>
          {order.dataStatus === 'changes_requested' && order.returnComment ? (
            <Alert variant="info" title="Требуются правки" className="mb-4">
              {order.returnComment}
            </Alert>
          ) : null}
          {order.dataStatus === 'filling' && !submittedView ? (
            // Заказ «На заполнении» (training-order-filling): заявка у менеджера, клиент довносит
            // данные; договор и счёт менеджер приложит сюда (В16).
            <div className="mb-4 flex flex-col gap-4">
              <Alert variant="info" title="Заявка отправлена менеджеру">
                {REQUEST_SENT_NOTICE}
              </Alert>
              <TrainingPaperwork documents={order.documents} emptyText={PAPERWORK_PENDING} />
            </div>
          ) : null}
          {order.dataStatus === 'draft' ? (
            <TrainingStartNotice state={startState} className="mb-4" />
          ) : null}
          {order.trainingChange ? (
            <TrainingChangeNotice
              change={order.trainingChange}
              side="client"
              onAction={decideChange}
              className="mb-4"
            />
          ) : null}
          {/* История изменений состава (ответ заказчицы 04.11 к п.1.4) — у заявки «На заполнении». */}
          {submittedView ? null : (
            <TrainingChangeHistory history={order.trainingChangeHistory} side="client" className="mb-4" />
          )}
          {editing && order.canProposeChange ? (
            <TrainingChangeEditor
              orderId={order.id}
              side="client"
              submitted={false}
              initial={{ programs: wizard.programs, packages: wizard.packages }}
              employees={wizard.selectedEmployees}
              propose={async (items) => {
                let next: OrderDetail
                try {
                  next = await trainingApi.proposeChange(order.id, items)
                } catch (err) {
                  // 409 — заказ изменился с другой стороны (например, Центрика уже предложила
                  // изменения): заказ и мастер перечитываются, редактор закрывается.
                  if (err instanceof ApiError && err.status === 409) {
                    let fresh: OrderDetail | null = null
                    try {
                      fresh = await ordersApi.get(order.id)
                    } catch {
                      // Перечитать не удалось — ошибка остаётся в редакторе.
                    }
                    if (fresh) {
                      await applyChanged(fresh)
                      return
                    }
                  }
                  throw err
                }
                await applyChanged(next)
              }}
              onCancel={() => setEditing(false)}
            />
          ) : (
            <TrainingRequest
              key={wizardVersion}
              order={order}
              wizard={wizard}
              initialStep={step}
              onProposeChange={order.canProposeChange ? () => setEditing(true) : undefined}
              presetPersonId={startState.presetPerson?.id}
              onStepChange={setStep}
              onWizardChange={setWizard}
              onOrderChanged={(updated) => {
                setOrder(updated)
                // Счётчик заказов, ждущих клиента, в меню (centrika-docs!98): заказ мог стать «На
                // заполнении» — перечитываем контекст кабинета.
                void refresh()
              }}
              onSubmitted={() => {
                setJustSubmitted(true)
                // Счётчик заказов, ждущих клиента, в меню (centrika-docs!98) — после отправки
                // перечитываем контекст кабинета.
                void refresh()
              }}
            />
          )}
        </>
      )}
    </div>
  )
}
